import { useMediaQuery } from "react-responsive";

import {
  Badge,
  Button,
  Card,
  Heading,
  List,
  MediaTile,
  Span,
} from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { useThumbtackModalContext } from "components/thumbtack-modal";
import { screenSizes } from "consts";
import type {
  InspectionAssetSource,
  KeystoneReferral,
} from "data/keystone-api-mapping";
import { usePolicyDetail } from "data/ss-data-hooks";
import {
  capitalize,
  checkForNaStringValue,
  formatDate,
  localTimeFromTimestamp,
} from "utils";

import { useInspectionsData } from "./hooks";
import type { GalleryOpenPayload } from "./index";
import { type UploadDrawerData } from "./upload-drawer";
import styles from "./write-out-card.module.scss";

export const WriteOutCard = ({
  referral,
  showGallery,
  showUploadDrawer,
}: {
  referral: KeystoneReferral;
  showGallery: (payload: GalleryOpenPayload) => void;
  showUploadDrawer: (uploadData: UploadDrawerData) => void;
}) => {
  const { data } = usePolicyDetail();
  const { latestInspection } = useInspectionsData();
  const { zip: zipCode } = data?.userDetails?.propertyAddress ?? {};
  const { agencyName } = data?.userInsuranceRep ?? {};
  const { openThumbtackModal } = useThumbtackModalContext();
  const isMobile = useMediaQuery({ maxWidth: screenSizes.tablet });
  const maxPhotosInRow = isMobile ? 3 : 5;
  const inspectionId = latestInspection?.id ?? "";
  const inspectionAssetObjects =
    latestInspection?.review?.inspectionReviewAssets ?? [];
  const {
    actionStatement,
    category,
    groupedIssueStatements,
    groupedReasonings,
    inspectionReviewAssetIds,
    inspectionReviewProofAssetIds,
    issueStatement,
    locationDetail,
    reasoning,
    searchTerm,
  } = referral;

  let writeOutHeading: string | null =
    issueStatement?.replace(/^.*? - /, "") ?? "";
  if (groupedIssueStatements?.length) {
    writeOutHeading = "Multiple issues";
  }
  writeOutHeading = checkForNaStringValue(writeOutHeading);
  const reasoningDisplay = checkForNaStringValue(reasoning ?? "");
  let reasonings: string[] = [];
  if (groupedReasonings?.length) {
    reasonings = groupedReasonings;
  } else if (reasoningDisplay) {
    reasonings = [reasoningDisplay];
  }
  const locationDisplay = checkForNaStringValue(locationDetail ?? "");
  const categoryDisplay = checkForNaStringValue(category ?? "");
  const actionStatementDisplay = checkForNaStringValue(actionStatement ?? "");
  const validSearchTerm = checkForNaStringValue(searchTerm ?? "");
  const subHeading: string = categoryDisplay
    ? `${categoryDisplay} ${locationDisplay ? `• ${locationDisplay}` : ""}`
    : "";
  const isReferralSubmitted: boolean =
    inspectionReviewProofAssetIds?.length > 0;

  const latestProofAsset = inspectionAssetObjects.find(
    (asset) => asset.id === inspectionReviewProofAssetIds?.[0],
  );
  const latestUploadDate = formatDate(
    latestProofAsset?.lastUpdatedTimestamp ?? "",
  );
  const latestUploadTime = localTimeFromTimestamp(
    latestProofAsset?.lastUpdatedTimestamp ?? "",
  );
  const assetSourceDisplay: Record<InspectionAssetSource, string> = {
    POLICYHOLDER: latestProofAsset?.lastUpdatedByAppUserId ?? "",
    PRODUCER: agencyName ? `a representative at ${agencyName}` : "",
    UNDERWRITER: "SageSure support",
    VENDOR: "",
  };
  const latestUploaderDisplay =
    assetSourceDisplay[
      latestProofAsset?.assetSource as InspectionAssetSource
    ] ?? "";

  const renderAssetList = ({
    assetIds,
    heading,
    openTabIndex,
  }: {
    assetIds?: string[];
    heading: string;
    openTabIndex: 0 | 1;
  }) => {
    if (!assetIds?.length) return null;

    const visibleAssetIds =
      assetIds.length > maxPhotosInRow
        ? assetIds.slice(0, maxPhotosInRow - 1)
        : assetIds;
    const galleryPayload: GalleryOpenPayload = {
      inspectionAssetIds: inspectionReviewAssetIds ?? [],
      proofAssetIds: inspectionReviewProofAssetIds ?? [],
      initialTabIndex: openTabIndex,
    };

    return (
      <div className={styles.fileListContainer}>
        <Span bold>{heading}</Span>
        <ul className={styles.fileList}>
          {visibleAssetIds.map((assetId) => (
            <li className={styles.fileListItem} key={assetId}>
              <MediaTile
                source={`/api/inspection/image/${assetId}`}
                orientation="landscape"
                imageFit="cover"
                description={`Inspection asset: ${assetId}`}
                onClick={() => showGallery(galleryPayload)}
                className={styles.fileTile}
              />
            </li>
          ))}
          {assetIds.length > maxPhotosInRow ? (
            <li className={styles.fileTileOverflow}>
              <MediaTile.Overflow
                onClick={() => showGallery(galleryPayload)}
                text={`+${assetIds.length - (maxPhotosInRow - 1)} more`}
              />
            </li>
          ) : null}
        </ul>
      </div>
    );
  };

  return (
    <Card>
      <Stack gap="sm">
        <div className={styles.heading}>
          <div className={styles.titleAndSubheading}>
            {writeOutHeading ? (
              <Heading size="sm">{capitalize(writeOutHeading)}</Heading>
            ) : null}
            <Span color="quiet" size="sm">
              {subHeading}
            </Span>
          </div>

          <div className={styles.statusAndButton}>
            <Span size="sm" className={styles.status}>
              <Badge.Indicator
                size="medium"
                appearance={isReferralSubmitted ? "success" : "warning"}
              />
              {isReferralSubmitted ? "Submitted" : "Not started"}
            </Span>
            <Button
              size="sm"
              fluid={isMobile}
              onPress={() =>
                showUploadDrawer({
                  issue: writeOutHeading ?? "",
                  referralId: referral?.id ?? "",
                  inspectionId,
                  subHeading,
                })
              }
            >
              Resolve issue
            </Button>
          </div>
        </div>

        {actionStatementDisplay ? (
          <div className={styles.resolveBox}>
            <Span bold>How to resolve:</Span>
            <List type="ul">
              <li className={styles.listItem}>{actionStatementDisplay}</li>
            </List>

            {validSearchTerm ? (
              <Span>
                Need help?{" "}
                <Button
                  appearance="link"
                  onPress={() =>
                    openThumbtackModal(zipCode ?? "", validSearchTerm)
                  }
                  data-testid={`write-out-contractor-button: "${validSearchTerm}"`}
                >
                  Find a qualified contractor.
                </Button>
              </Span>
            ) : null}
          </div>
        ) : null}

        {reasonings.length ? (
          <div className={styles.reasoning}>
            <Span bold>Why is this an issue?</Span>
            {reasonings.map((reasoningString, idx) => (
              <Span key={idx}>{reasoningString}</Span>
            ))}
          </div>
        ) : null}

        {renderAssetList({
          assetIds: inspectionReviewAssetIds,
          heading: "Inspection photos",
          openTabIndex: 0,
        })}

        {renderAssetList({
          assetIds: inspectionReviewProofAssetIds,
          heading: "Proof uploads",
          openTabIndex: 1,
        })}
        {latestProofAsset && latestUploadDate ? (
          <Span color="quiet">
            Last updated{" "}
            {latestUploaderDisplay ? `by ${latestUploaderDisplay} ` : null}
            on {latestUploadDate}
            {latestUploadTime ? ` at ${latestUploadTime.toLowerCase()}` : null}
          </Span>
        ) : null}
      </Stack>
    </Card>
  );
};
